import { inject, Injectable } from '@angular/core';
import { Action, Actions, ofActionDispatched, Selector, State, StateContext } from '@ngxs/store';
import { finalize, Observable, switchMap, takeUntil, tap } from 'rxjs';
import { Notification } from '@layout/notifications/models/notification.interface';
import { NotificationsService } from '@layout/notifications/services/notifications.service';
import {
  DeleteAllNotifications,
  DeleteNotification,
  LoadNotifications,
  LoadUnreadNotificationsCount,
  LoadZendeskSsoRequestInfo,
  LoadZendeskSsoRequestInfoSuccess,
  MarkAllNotificationsAsViewed,
  SetNotificationsVisibility,
  StartListeningNotifications,
  StopListeningNotifications,
  ToggleNotificationViewability,
} from '@layout/notifications/store/actions/notifications.actions';
import { ZendeskSsoRequestInfo } from '@core/sso/models/zendesk/zendesk-sso-request-info.interface';
import { webSocket } from 'rxjs/webSocket';

interface NotificationsStateModel {
  notifications?: Notification[];
  areNotificationsVisible: boolean;
  unreadNotificationsCount?: number | undefined;
  loading: boolean;
}

@State<NotificationsStateModel>({
  name: 'notifications',
  defaults: {
    loading: false,
    areNotificationsVisible: false,
  },
})
@Injectable()
export class NotificationsState {
  private readonly notificationsService = inject(NotificationsService);
  private readonly actions$ = inject(Actions);

  @Selector()
  static notifications(state: NotificationsStateModel): Notification[] | undefined {
    return state.notifications;
  }

  @Selector()
  static unreadNotificationsCount(state: NotificationsStateModel): number | undefined {
    return state.unreadNotificationsCount;
  }

  @Selector()
  static areNotificationsVisible(state: NotificationsStateModel): boolean {
    return state.areNotificationsVisible;
  }

  @Selector()
  static loading(state: NotificationsStateModel): boolean {
    return state.loading;
  }

  @Action(LoadNotifications, { cancelUncompleted: true })
  loadNotifications(context: StateContext<NotificationsStateModel>, { withLoading }: LoadNotifications): Observable<Notification[]> {
    if (withLoading) {
      context.patchState({ loading: true });
    }

    return this.notificationsService.getNotifications().pipe(
      tap(notifications => context.patchState({ notifications })),
      finalize(() => context.patchState({ loading: false }))
    );
  }

  @Action(LoadUnreadNotificationsCount, { cancelUncompleted: true })
  loadUnreadNotificationsCount(context: StateContext<NotificationsStateModel>): Observable<number> {
    return this.notificationsService
      .getUnreadNotificationsCount()
      .pipe(tap(unreadNotificationsCount => context.patchState({ unreadNotificationsCount })));
  }

  @Action(StartListeningNotifications, { cancelUncompleted: true })
  startListeningNotifications(context: StateContext<NotificationsStateModel>): Observable<void> {
    return this.notificationsService
      .getUserPubSubClientAccessUrl()
      .pipe(switchMap((url: string) => this.listenNotifications$(url, context)));
  }

  @Action(MarkAllNotificationsAsViewed)
  markAllNotificationsAsViewed(): Observable<void> {
    return this.notificationsService.markAllNotificationsAsRead();
  }

  @Action(ToggleNotificationViewability)
  toggleNotificationViewability(
    context: StateContext<NotificationsStateModel>,
    { notificationId, source, viewed }: ToggleNotificationViewability
  ): Observable<void> {
    return this.notificationsService.updateNotificationViewability(notificationId, source, viewed);
  }

  @Action(SetNotificationsVisibility)
  setNotificationsVisibility(
    context: StateContext<NotificationsStateModel>,
    { areNotificationsVisible }: SetNotificationsVisibility
  ): void {
    context.patchState({ areNotificationsVisible });

    if (areNotificationsVisible) {
      context.dispatch(new LoadNotifications());
    }
  }

  @Action(DeleteNotification)
  deleteNotification(context: StateContext<NotificationsStateModel>, { notificationId, source }: DeleteNotification): Observable<void> {
    return this.notificationsService.deleteNotification(notificationId, source);
  }

  @Action(DeleteAllNotifications)
  deleteAllNotifications(): Observable<void> {
    return this.notificationsService.deleteAllNotifications();
  }

  @Action(LoadZendeskSsoRequestInfo)
  loadZendeskSsoRequestInfo(
    context: StateContext<NotificationsStateModel>,
    { ticketId }: LoadZendeskSsoRequestInfo
  ): Observable<ZendeskSsoRequestInfo> {
    return this.notificationsService
      .getZendeskSsoRequestInfo(ticketId)
      .pipe(tap(zendeskSsoRequestInfo => context.dispatch(new LoadZendeskSsoRequestInfoSuccess(zendeskSsoRequestInfo))));
  }

  private listenNotifications$(url: string, context: StateContext<NotificationsStateModel>): Observable<void> {
    const stopListeningNotifications$ = this.actions$.pipe(ofActionDispatched(StopListeningNotifications));

    return webSocket<void>(url).pipe(
      tap(() => {
        context.dispatch(new LoadUnreadNotificationsCount());

        if (context.getState().areNotificationsVisible) {
          context.dispatch(new LoadNotifications(false));
        }
      }),
      takeUntil(stopListeningNotifications$)
    );
  }
}
